import React from 'react';
import { 
  Twitter, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Youtube,
  Clock,
  Calendar
} from 'lucide-react';

interface PlatformScheduleInfoProps {
  onClose: () => void;
}

const PlatformScheduleInfo: React.FC<PlatformScheduleInfoProps> = ({ onClose }) => {
  const platformInfo = [
    {
      name: 'X (Twitter)',
      icon: Twitter,
      color: '#1DA1F2',
      bestTimes: 'Weekdays between 9 AM – 11 AM',
      bestDays: 'Wednesdays and Fridays',
      notes: 'Engagement peaks during mid-morning hours when professionals check social media before diving into work.'
    },
    {
      name: 'Instagram',
      icon: Instagram,
      color: '#E4405F',
      bestTimes: '9 AM – 12 PM and around 6 PM',
      bestDays: 'Tuesdays, Wednesdays, and Thursdays',
      notes: 'Morning posts catch users during commutes, while evening posts reach users after work hours.'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: '#1877F2',
      bestTimes: '8 AM – 10 AM and 1 PM – 3 PM',
      bestDays: 'Tuesdays and Thursdays',
      notes: 'Mid-week posts typically see higher engagement than weekend content.'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: '#0A66C2',
      bestTimes: '7 AM – 9 AM or 4 PM – 6 PM',
      bestDays: 'Tuesdays through Thursdays',
      notes: 'Professional audience engages most during business hours and early mornings.'
    },
    {
      name: 'YouTube',
      icon: Youtube,
      color: '#FF0000',
      bestTimes: '12 PM – 3 PM',
      bestDays: 'Thursdays and Fridays',
      notes: 'Uploading before the weekend allows time for indexing and maximizes weekend viewing.'
    }
  ];

  return (
    <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Optimal Posting Times
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
        >
          <span className="text-sm">Close</span>
        </button>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400">
        These recommendations are based on industry research across various platforms. Your specific audience may have different engagement patterns.
      </p>

      <div className="space-y-4">
        {platformInfo.map((platform) => {
          const Icon = platform.icon;
          return (
            <div 
              key={platform.name}
              className="p-4 rounded-lg border border-gray-200 dark:border-dark-700"
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-1.5 rounded-full" style={{ backgroundColor: `${platform.color}20` }}>
                  <Icon className="h-5 w-5" style={{ color: platform.color }} />
                </div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {platform.name}
                </h4>
              </div>
              
              <div className="ml-10 space-y-2">
                <div className="flex items-start space-x-2">
                  <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Best Times:</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{platform.bestTimes}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-2">
                  <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Best Days:</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{platform.bestDays}</p>
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  {platform.notes}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlatformScheduleInfo;